import React, { useEffect, useRef, useState } from "react";
import { useRouter } from "next/router";
import { renderToString } from "react-dom/server";
import InfoWindowContent from "./infoWindow";
import { getLocation } from "@/src/lib/util/location";
import {
    loadGoogleMapsScript,
    createMap,
    createMarker,
    createInfoWindow,
} from "@/src/lib/util/googleMapsUtils";
import {
    DEFAULT_CENTER,
    GOOGLE_MAPS_API_KEY_2,
    NEXT_IMAGE_CDN,
} from "@/src/lib/util/config";
import useBreakpoint from "@/src/lib/util/custom-hooks/useBreakPoint";
import Progressbar from "@/src/components/loading";

const mapStyles = [
    {
        featureType: "poi",
        elementType: "labels",
        stylers: [{ visibility: "off" }],
    },
    {
        featureType: "poi.business",
        stylers: [{ visibility: "off" }],
    },
];

const MapView = ({ centers, selectedCenter, setCurrentSlug }) => {
    const router = useRouter();
    const isMobile = useBreakpoint("sm");
    const mapRef = useRef(null);
    const mapInstanceRef = useRef(null);
    const infoWindowRef = useRef(null);

    // State
    const [mapCenter, setMapCenter] = useState(null);

    // Computed values
    const isDataLoading = !centers || centers.length === 0;

    // Get user's location or fallback to default
    useEffect(() => {
        getLocation()
            .then((pos) => setMapCenter(pos))
            .catch(() => setMapCenter(DEFAULT_CENTER));
    }, []);

    // Helper functions
    const getMarkerIcon = (centreType) => {
        return centreType === "LAB"
            ? `${NEXT_IMAGE_CDN}/map-icons/mri.png`
            : `${NEXT_IMAGE_CDN}/map-icons/blood.png`;
    };

    const createMapOptions = (center) => {
        const fallbackCenter = {
            lat: 28.6139, // Delhi as fallback
            lng: 77.209,
        };

        const finalCenter = {
            lat: parseFloat(center?.lat ?? mapCenter?.lat),
            lng: parseFloat(center?.lng ?? mapCenter?.lng),
        };

        if (!isFinite(finalCenter.lat) || !isFinite(finalCenter.lng)) {
            console.warn("Invalid center coordinates, using fallback");
            return {
                center: fallbackCenter,
                zoom: 12,
                disableDefaultUI: true,
                clickableIcons: false,
                styles: mapStyles,
                fullscreenControl: true,
                cameraControl: true,
                scaleControl: true,
                streetViewControl: true,
            };
        }

        return {
            center: finalCenter,
            zoom: 12,
            disableDefaultUI: true,
            clickableIcons: false,
            styles: mapStyles,
            fullscreenControl: true,
            cameraControl: true,
            scaleControl: true,
            streetViewControl: true,
        };
    };

    const createInfoWindowOptions = (center) => {
        const options = {
            content: renderToString(
                <InfoWindowContent center={center} isMobile={isMobile} />
            ),
            position: {
                lat: center.latitude,
                lng: center.longitude,
            },
            maxWidth: 500,
        };
        
        if (window.google && window.google.maps) {
            options.pixelOffset = new window.google.maps.Size(0, -20);
        }
        
        return options;
    };

    const addWhatsAppEventListener = () => {
        setTimeout(() => {
            const whatsappIcon = document.getElementById("whatsapp-icon");
            if (whatsappIcon) {
                whatsappIcon.addEventListener("click", () => {
                    const whatsappUrl =
                        whatsappIcon.getAttribute("data-whatsapp-url");
                    if (whatsappUrl) {
                        window.open(whatsappUrl, "_blank");
                    }
                });
            }
        }, 100);
    };

    const handleMarkerClick = (center) => {
        const route = center.id.split("/");
        if (typeof setCurrentSlug === "function") {
            setCurrentSlug(center.id);
        }

        // Pan map to marker location
        if (mapInstanceRef.current) {
            mapInstanceRef.current.panTo({
                lat: center.latitude,
                lng: center.longitude,
            });
        }

        // Close existing info window
        if (infoWindowRef.current) {
            infoWindowRef.current.close();
        }

        // Create and open new info window
        const infoWindow = createInfoWindow(createInfoWindowOptions(center));
        infoWindow.open(mapInstanceRef.current);
        infoWindowRef.current = infoWindow;

        // Add WhatsApp event listener
        addWhatsAppEventListener();

        // Navigate to center details page
        router.push(`/locate-us/${route[0]}/${route[1]}`);
    };

    const createMarkers = () => {
        centers.forEach((center) => {
            const marker = createMarker(
                mapInstanceRef.current,
                {
                    lat: center.latitude,
                    lng: center.longitude,
                },
                {
                    icon: getMarkerIcon(center.centreType),
                    zIndex: center.centreType === "LAB" ? 10 : 5,
                }
            );

            marker.addListener("click", () => handleMarkerClick(center));
        });
    };

    const openSelectedCenterInfoWindow = () => {
        if (!selectedCenter) return;

        // Pan to selected center
        mapInstanceRef.current.panTo({
            lat: selectedCenter.latitude,
            lng: selectedCenter.longitude,
        });

        // Create and open info window
        const infoWindow = createInfoWindow(
            createInfoWindowOptions(selectedCenter)
        );
        infoWindow.open(mapInstanceRef.current);
        infoWindowRef.current = infoWindow;

        // Add WhatsApp event listener
        addWhatsAppEventListener();
    };

    // Initialize map and markers
    useEffect(() => {
        if (!mapCenter || isDataLoading) return;

        if (!GOOGLE_MAPS_API_KEY_2) {
            console.error("Google Maps API key is missing. Please check NEXT_PUBLIC_GOOGLE_MAPS_API_KEY_2_GEOCODING in your environment variables.");
            return;
        }

        loadGoogleMapsScript(GOOGLE_MAPS_API_KEY_2, [])
            .then(() => {
                // Create map instance
                mapInstanceRef.current = createMap(
                    mapRef.current,
                    createMapOptions(selectedCenter)
                );

                // Create markers for all centers
                createMarkers();

                // Open info window for selected center if provided
                openSelectedCenterInfoWindow();
            })
            .catch((error) => {
                console.error("Failed to load Google Maps:", error);
                console.error("Please verify your Google Maps API key and ensure the Maps JavaScript API is enabled in Google Cloud Console.");
            });
    }, [mapCenter, centers, selectedCenter]);

    // Loading state
    if (isDataLoading || !mapCenter) {
        return (
            <div className="max-w-6xl mx-auto">
                <div className="flex flex-col gap-2 justify-center items-center h-full">
                    <Progressbar size={40} />
                </div>
            </div>
        );
    }

    return <div ref={mapRef} style={{ width: "100%", height: "500px" }} />;
};

export default MapView;
